import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { CONTACT_INFO, BRAND_INFO } from '../data/mock';
import { ShieldCheck, CheckCircle, RotateCcw, Phone, Mail, ArrowRight, Award, Heart } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const GuaranteePage = () => {
  usePageTitle('Our Guarantee', 'Stitch Your Dash satisfaction guarantee. 30-day returns, quality products, and dedicated customer support.');

  const guarantees = [
    {
      icon: ShieldCheck,
      title: '30-Day Money-Back Guarantee',
      description: 'If you\'re not completely satisfied with your purchase, return it within 30 days for a full refund. No questions asked.'
    },
    {
      icon: Award,
      title: 'Quality Assurance',
      description: 'Every product is carefully inspected before shipping to ensure it meets our high standards of quality.'
    },
    {
      icon: RotateCcw,
      title: 'Hassle-Free Returns',
      description: 'We make returns easy. Simply contact us, and we\'ll provide a prepaid return shipping label.'
    },
    {
      icon: Heart,
      title: 'Customer Satisfaction',
      description: 'Your satisfaction is our top priority. We\'re not happy unless you\'re happy with your purchase.'
    }
  ];

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Hero with Banner Image */}
        <section className="relative bg-warmBrown-800 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.pexels.com/photos/6843239/pexels-photo-6843239.jpeg?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-warmBrown-900 via-warmBrown-900/90 to-warmBrown-800/70" />
          </div>
          <div className="absolute inset-0 pattern-dots opacity-20" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-2xl">
              <div className="w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheck className="w-8 h-8 text-rose-400" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4">Our Guarantee</h1>
              <p className="text-warmBrown-300 text-lg">Shop with complete confidence. We stand behind every product we sell.</p>
            </div>
          </div>
        </section>

        {/* Main Guarantee Banner */}
        <section className="py-12 bg-rose-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl text-white mb-4">100% Satisfaction Guaranteed</h2>
            <p className="text-rose-100 text-lg">We're confident you'll love our products. If not, we'll make it right.</p>
          </div>
        </section>

        {/* Guarantees Grid */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {guarantees.map((item, index) => (
                <div key={index} className="bg-white rounded-3xl p-8 shadow-card border border-cream-200">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-7 h-7 text-rose-500" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl text-warmBrown-900 mb-3">{item.title}</h3>
                      <p className="text-warmBrown-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 lg:py-20 bg-cream-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl text-warmBrown-900 text-center mb-12">How Our Guarantee Works</h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 flex items-start gap-5">
                <span className="w-10 h-10 bg-rose-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</span>
                <div>
                  <h3 className="font-semibold text-warmBrown-900 mb-2">Purchase with Confidence</h3>
                  <p className="text-warmBrown-600">Every purchase is protected by our 30-day money-back guarantee.</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 flex items-start gap-5">
                <span className="w-10 h-10 bg-rose-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</span>
                <div>
                  <h3 className="font-semibold text-warmBrown-900 mb-2">Not Satisfied?</h3>
                  <p className="text-warmBrown-600">Contact our customer service team within 30 days of delivery.</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 flex items-start gap-5">
                <span className="w-10 h-10 bg-rose-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</span>
                <div>
                  <h3 className="font-semibold text-warmBrown-900 mb-2">Easy Return Process</h3>
                  <p className="text-warmBrown-600">We'll send you a prepaid return label. Simply pack and ship the item back.</p>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 flex items-start gap-5">
                <span className="w-10 h-10 bg-rose-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</span>
                <div>
                  <h3 className="font-semibold text-warmBrown-900 mb-2">Get Your Refund</h3>
                  <p className="text-warmBrown-600">Once we receive the item, your refund is processed within 5-7 business days.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl text-warmBrown-900 mb-6">Questions About Our Guarantee?</h2>
            <p className="text-warmBrown-600 mb-8">Our customer service team is here to help.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 text-rose-600 hover:text-rose-700 font-semibold text-lg">
                <Phone className="w-6 h-6" /> {CONTACT_INFO.phone}
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-3 text-rose-600 hover:text-rose-700 font-semibold text-lg">
                <Mail className="w-6 h-6" /> {CONTACT_INFO.email}
              </a>
            </div>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20 group"
            >
              Shop with Confidence
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GuaranteePage;
